import Link from 'next/link';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';

/*
 * Le bouton, spec-design.md v2 section « Bouton ».
 *
 * TROIS VARIANTES, ET ELLES NE SONT PAS INTERCHANGEABLES :
 *   - `principal` : l'appel a l'action. Fond `action`, texte `texte-inverse`, ratio mesure 4,90.
 *   - `discret`   : la barre de navigation. Aucun fond, bordure, texte `texte-accent`.
 *   - `inverse`   : sur un fond contraste, la ou le fond `action` ne se detacherait pas.
 *
 * IL REND SOIT UN LIEN, SOIT UN BOUTON, selon qu'on lui donne une cible ou non. Ce n'est pas une
 * commodite : AC-DSN-022 compte les appels a l'action de la page d'accueil, et depuis le
 * 2026-08-15, tache 07-09, il en exige SIX vers `/inscription` et ZERO vers `#reservation`. Les six
 * portent le meme libelle et menent au meme endroit ; deux d'entre eux y menaient en deux clics
 * jusqu'a cette date.
 *
 * `repere` POSE `data-bouton`, et c'est le point d'accroche des controles au navigateur. Le
 * selecteur donne a l'outil de pilotage ne doit porter aucune espace, ce que `agent-browser`
 * couperait en deux arguments : un attribut sur le bouton lui-meme evite `[x] a`.
 *
 * LE FOCUS EST VISIBLE ET IL NE SE RETIRE PAS : contour de 2 pixels en `focus`, a 3 pixels de
 * distance. spec-technique.md section « L'accessibilite ».
 */
const variantes = cva(
  cn(
    'inline-flex items-center justify-center text-center',
    'font-corps font-semibold text-bouton',
    'px-8 py-4 rounded-carte',
    'transition-colors duration-150 ease-douce',
    'focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus',
  ),
  {
    variants: {
      variante: {
        principal: 'bg-action text-texte-inverse hover:bg-action-survol',
        // FOND BLANC DEPUIS LE 2026-08-14, demande de Wahid : « bouton topbar, fond blanc ». Il
        // n'en avait aucun, et sur la barre de navigation, qui est elle-meme un creme translucide
        // pose sur le creme de la page, un bouton sans fond ne se detachait d'aucun des deux.
        discret:
          'border border-bordure-forte bg-fond-eleve text-texte-accent hover:border-action hover:bg-fond-eleve hover:text-action-survol',
        inverse: 'bg-fond-eleve text-texte-fort hover:bg-fond-inerte',
      },
      /** Sur telephone, le bouton principal occupe toute la largeur de la colonne. */
      pleineLargeur: {
        true: 'w-full md:w-auto',
        false: 'w-auto',
      },
      /** La barre de navigation et la barre d action ont besoin d un bouton plus court. */
      compact: {
        true: 'px-5 py-3 text-navigation',
        false: '',
      },
    },
    defaultVariants: { variante: 'principal', pleineLargeur: false, compact: false },
  },
);

type ProprietesDeBouton = VariantProps<typeof variantes> & {
  readonly libelle: string;
  /** Une cible rend un lien. Son absence rend un bouton d envoi. */
  readonly href?: string;
  /** Valeur de `data-bouton`, le point d accroche des controles au navigateur. */
  readonly repere: string;
  readonly className?: string;
};

export default function Bouton({
  libelle,
  href,
  repere,
  variante,
  pleineLargeur,
  compact,
  className,
}: ProprietesDeBouton) {
  const classes = cn(variantes({ variante, pleineLargeur, compact }), className);

  if (href) {
    return (
      <Link href={href} data-bouton={repere} data-composant="Bouton" className={classes}>
        {libelle}
      </Link>
    );
  }

  return (
    <button type="submit" data-bouton={repere} data-composant="Bouton" className={classes}>
      {libelle}
    </button>
  );
}
